import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { storyAppealService, AuthorStoryStatus } from '../services/storyAppealService';
import { LoadingSpinner } from '../shared/components';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  color: var(--color-gray-600);
  font-size: 1.1rem;
`;

const StoryGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const StoryCard = styled.div<{ $status: string }>`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  border-left: 4px solid ${props => {
    switch (props.$status) {
      case 'DRAFT': return '#6B7280';
      case 'PENDING_REVIEW':
      case 'AI_REVIEW_PENDING': return '#3B82F6';
      case 'ADMIN_REVIEW_PENDING': return '#F59E0B';
      case 'REJECTED': return '#EF4444';
      case 'APPROVED':
      case 'PUBLISHED': return '#10B981';
      default: return '#6B7280';
    }
  }};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const StoryTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${props => {
    switch (props.$status) {
      case 'DRAFT':
        return `background: #F3F4F6; color: #374151;`;
      case 'PENDING_REVIEW':
      case 'AI_REVIEW_PENDING':
        return `background: #DBEAFE; color: #1E40AF;`;
      case 'ADMIN_REVIEW_PENDING':
        return `background: #FEF3C7; color: #92400E;`;
      case 'REJECTED':
        return `background: #FEE2E2; color: #991B1B;`;
      case 'APPROVED':
      case 'PUBLISHED':
        return `background: #D1FAE5; color: #065F46;`;
      default:
        return `background: #F3F4F6; color: #374151;`;
    }
  }}
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

const RejectionDetails = styled.div`
  background: var(--color-red-50);
  border: 1px solid var(--color-red-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const RejectionTitle = styled.div`
  font-weight: 600;
  color: var(--color-red-800);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RejectionReason = styled.div`
  color: var(--color-red-700);
  font-size: 0.875rem;
  line-height: 1.5;
`;

const AIAnalysisCard = styled.div`
  background: var(--color-blue-50);
  border: 1px solid var(--color-blue-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const AIHeader = styled.div`
  font-weight: 600;
  color: var(--color-blue-800);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ViolationsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const ViolationTag = styled.span`
  padding: 0.25rem 0.5rem;
  background: var(--color-red-100);
  color: var(--color-red-800);
  border-radius: var(--border-radius-sm);
  font-size: 0.75rem;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
          &:hover:not(:disabled) {
            background: var(--color-primary-dark);
          }
        `;
      case 'danger':
        return `
          background: var(--color-red-600);
          border-color: var(--color-red-600);
          color: white;
          &:hover:not(:disabled) {
            background: var(--color-red-700);
          }
        `;
      default:
        return `
          background: white;
          border-color: var(--color-gray-300);
          color: var(--color-gray-700);
          &:hover:not(:disabled) {
            background: var(--color-gray-50);
          }
        `;
    }
  }}
`;

const AppealStatus = styled.div<{ $status: string }>`
  padding: 0.5rem;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  
  ${props => {
    switch (props.$status) {
      case 'PENDING':
        return `background: var(--color-yellow-100); color: var(--color-yellow-800);`;
      case 'APPROVED':
        return `background: var(--color-green-100); color: var(--color-green-800);`;
      case 'REJECTED':
        return `background: var(--color-red-100); color: var(--color-red-800);`;
      default:
        return `background: var(--color-gray-100); color: var(--color-gray-800);`;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-gray-500);
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalLabel = styled.label`
  display: block;
  font-weight: 500;
  color: var(--color-gray-700);
  margin-bottom: 0.5rem;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-gray-400);
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const MyStoryStatuses: React.FC = () => {
  const [stories, setStories] = useState<AuthorStoryStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingAppeal, setSubmittingAppeal] = useState<string | null>(null);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealData, setAppealData] = useState<{ storyId: string; storyTitle: string } | null>(null);
  const [appealReason, setAppealReason] = useState('');

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      setLoading(true);
      const response = await storyAppealService.getMyStoryStatuses();
      
      // Transform regular story data to AuthorStoryStatus format
      const transformedStories = response.data.map((story: any) => ({
        id: story.id,
        title: story.title,
        status: story.publicationStatus as 'DRAFT' | 'PENDING_REVIEW' | 'AI_REVIEW_PENDING' | 'ADMIN_REVIEW_PENDING' | 'REJECTED' | 'APPROVED' | 'PUBLISHED',
        submittedAt: story.createdAt,
        reviewedAt: story.updatedAt !== story.createdAt ? story.updatedAt : null,
        rejectionReason: undefined, // Not available in StoryDto
        canAppeal: story.publicationStatus === 'REJECTED', // Can appeal rejected stories
        appealStatus: undefined,
        aiAnalysis: undefined // Not available in StoryDto
      }));
      
      setStories(transformedStories);
    } catch (error) {
      console.error('Failed to load story statuses:', error);
      toast.error('Failed to load your story statuses');
    } finally {
      setLoading(false);
    }
  };

  const openAppealModal = (storyId: string, storyTitle: string) => {
    setAppealData({ storyId, storyTitle });
    setAppealReason('');
    setShowAppealModal(true);
  };

  const closeAppealModal = () => {
    setShowAppealModal(false);
    setAppealData(null);
    setAppealReason('');
  };

  const handleSubmitAppeal = async () => {
    if (!appealData || !appealReason.trim()) {
      toast.error('Please provide a reason for your appeal');
      return;
    }

    try {
      setSubmittingAppeal(appealData.storyId);
      await storyAppealService.submitAppeal(appealData.storyId, {
        appealReason: appealReason.trim()
      });

      toast.success('Appeal submitted successfully! We will review it within 2-3 business days.');
      closeAppealModal();
      await loadStories(); // Refresh to update appeal status
    } catch (error) {
      console.error('Failed to submit appeal:', error);
      toast.error('Failed to submit appeal. Please try again.');
    } finally {
      setSubmittingAppeal(null);
    }
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      'DRAFT': 'Draft',
      'PENDING_REVIEW': 'Pending Review',
      'AI_REVIEW_PENDING': 'AI Review Pending',
      'ADMIN_REVIEW_PENDING': 'Admin Review Pending',
      'REJECTED': 'Rejected',
      'APPROVED': 'Approved',
      'PUBLISHED': 'Published'
    };
    return statusMap[status] || status;
  };

  const getStatusDescription = (status: string) => {
    const descriptions: Record<string, string> = {
      'DRAFT': 'Story is saved but not submitted for review',
      'PENDING_REVIEW': 'Story is queued for initial review',
      'AI_REVIEW_PENDING': 'Story is being analyzed by our AI content filter',
      'ADMIN_REVIEW_PENDING': 'Story requires human review by our moderation team',
      'REJECTED': 'Story was rejected and needs improvements',
      'APPROVED': 'Story has been approved and will be published soon',
      'PUBLISHED': 'Story is live and visible to readers'
    };
    return descriptions[status] || 'Status unknown';
  };

  if (loading) {
    return (
      <Container>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <LoadingSpinner message="Loading your stories..." />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>My Story Statuses</Title>
        <Description>
          Track the review progress of your submitted stories and manage appeals
        </Description>
      </Header>

      {stories.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
          <h3>No Stories Yet</h3>
          <p>You haven't submitted any stories for review yet.</p>
          <Button $variant="primary" onClick={() => window.location.href = '/story/create'}>
            Create Your First Story
          </Button>
        </EmptyState>
      ) : (
        <StoryGrid>
          {stories.map(story => (
            <StoryCard key={story.id} $status={story.status}>
              <CardHeader>
                <StoryTitle>{story.title}</StoryTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <StatusBadge $status={story.status}>
                    {getStatusLabel(story.status)}
                  </StatusBadge>
                  {story.appealStatus && (
                    <AppealStatus $status={story.appealStatus}>
                      Appeal: {story.appealStatus.toLowerCase()}
                    </AppealStatus>
                  )}
                </div>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <span>{getStatusDescription(story.status)}</span>
                  {story.submittedAt && (
                    <span>Submitted: {new Date(story.submittedAt).toLocaleDateString()}</span>
                  )}
                </InfoRow>

                {story.status === 'REJECTED' && story.rejectionReason && (
                  <RejectionDetails>
                    <RejectionTitle>
                      <span>❌</span>
                      Rejection Reason
                    </RejectionTitle>
                    <RejectionReason>{story.rejectionReason}</RejectionReason>
                  </RejectionDetails>
                )}

                {story.aiAnalysis && story.aiAnalysis.violations.length > 0 && (
                  <AIAnalysisCard>
                    <AIHeader>
                      <span>🤖</span>
                      AI Content Analysis
                    </AIHeader>
                    <ViolationsList>
                      {story.aiAnalysis.violations.map(violation => (
                        <ViolationTag key={violation}>
                          {violation.replace('_', ' ')}
                        </ViolationTag>
                      ))}
                    </ViolationsList>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-blue-700)', fontStyle: 'italic' }}>
                      "{story.aiAnalysis.explanation}"
                    </div>
                  </AIAnalysisCard>
                )}

                <ActionButtons>
                  <Button onClick={() => window.location.href = `/story/edit/${story.id}`}>
                    Edit Story
                  </Button>
                  
                  {story.canAppeal && story.status === 'REJECTED' && !story.appealStatus && (
                    <Button
                      $variant="primary"
                      onClick={() => openAppealModal(story.id, story.title)}
                      disabled={submittingAppeal === story.id}
                    >
                      {submittingAppeal === story.id ? 'Submitting...' : '📝 Submit Appeal'}
                    </Button>
                  )}

                  {story.appealStatus === 'PENDING' && (
                    <Button disabled>
                      ⏳ Appeal Under Review
                    </Button>
                  )}

                  {story.appealStatus === 'APPROVED' && (
                    <Button disabled>
                      ✅ Appeal Approved - Story Republished
                    </Button>
                  )}

                  {story.appealStatus === 'REJECTED' && (
                    <Button disabled>
                      ❌ Appeal Rejected
                    </Button>
                  )}
                </ActionButtons>
              </CardContent>
            </StoryCard>
          ))}
        </StoryGrid>
      )}

      {/* Appeal Modal */}
      {showAppealModal && appealData && (
        <ModalOverlay onClick={closeAppealModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Submit Appeal for "{appealData.storyTitle}"</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <ModalLabel htmlFor="appealReason">
                Why do you believe this story should be reconsidered?
              </ModalLabel>
              <ModalTextarea
                id="appealReason"
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                placeholder="Please provide a detailed explanation of why you believe your story was incorrectly rejected. Include specific reasons why the content is appropriate for children ages 5-12."
                autoFocus
              />
              <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                Our moderation team will review your appeal within 2-3 business days.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button onClick={closeAppealModal}>
                Cancel
              </Button>
              <Button
                $variant="primary"
                onClick={handleSubmitAppeal}
                disabled={!appealReason.trim() || submittingAppeal === appealData.storyId}
              >
                {submittingAppeal === appealData.storyId ? 'Submitting...' : 'Submit Appeal'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default MyStoryStatuses;